import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  ZoomIn,
  ZoomOut,
  Image,
  File,
  FileText,
  Video,
  Paperclip,
  Eye,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Attachment } from '@/types/ticket';
import {
  AuthenticatedMediaImage,
  AuthenticatedMediaVideo,
} from '@/components/common/AuthenticatedMediaImage';
import {
  collectAttachmentMediaPathsFromRecord,
  getCachedPdfBlobObjectUrl,
  isAttachmentMediaBlocked,
} from '@sd/lib/attachmentMediaUrl';

interface AttachmentViewerProps {
  attachments: Attachment[];
}

const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return Image;
  if (type.startsWith('video/')) return Video;
  if (type.includes('pdf') || type.includes('word') || type.includes('document')) return FileText;
  return File;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImage = (type: string) => type.startsWith('image/');
const isVideo = (type: string) => type.startsWith('video/');

/** Mime can be octet-stream from API — infer images from filename. */
function looksLikeImageAttachment(a: Attachment): boolean {
  if (isImage(a.type)) {
    return true;
  }
  const mime = (a.type ?? '').toLowerCase();
  if (mime.includes('octet-stream') || !mime.trim()) {
    return /\.(jpe?g|png|gif|webp|avif|bmp)$/i.test(a.name);
  }
  return false;
}

/** Mime can be octet-stream from API — infer videos (e.g. screen recordings) from filename. */
function looksLikeVideoAttachment(a: Attachment): boolean {
  if (isVideo(a.type)) {
    return true;
  }
  const mime = (a.type ?? '').toLowerCase();
  if (mime.includes('octet-stream') || !mime.trim()) {
    return /\.(webm|mp4|ogg|mov|avi)$/i.test(a.name);
  }
  return false;
}

/** Skip thumbnail URLs when loading full-size preview. */
function isThumbnailMediaPath(path: string): boolean {
  return /thumbnail|thumb|_tn\b|\/tn\/|size=small|w=\d{1,3}\b/i.test(path.trim());
}

/** Full-size paths first — same as admin `CustomFileUpload` / `AuthenticatedMediaImage`. */
function getAttachmentPreviewPaths(attachment: Attachment): {
  filePath: string;
  fallbackPaths: string[];
} {
  const id = attachment.id.trim();
  const isUuid = /^[0-9a-f-]{36}$/i.test(id);
  const seen = new Set<string>();
  const ordered: string[] = [];

  const push = (raw: string) => {
    const path = raw.trim();
    if (!path || isThumbnailMediaPath(path) || seen.has(path)) {
      return;
    }
    seen.add(path);
    ordered.push(path);
  };

  if (isUuid && !isAttachmentMediaBlocked(id)) {
    push(`/file-utility/files/${id}/download/`);
  }

  for (const path of collectAttachmentMediaPathsFromRecord(attachment, false)) {
    push(path);
  }

  if (attachment.url?.trim()) {
    push(attachment.url);
  }

  if (attachment.mediaUrl?.trim()) {
    push(attachment.mediaUrl);
  }

  const filePath = ordered[0] ?? attachment.url ?? "";
  return { filePath, fallbackPaths: ordered.slice(1) };
}

function isPDF(attachment: Pick<Attachment, 'type' | 'name'>): boolean {
  const mime = (attachment.type ?? '').toLowerCase();
  if (mime === 'application/pdf') {
    return true;
  }
  if (mime.includes('octet-stream') || !mime.trim()) {
    return /\.pdf$/i.test(attachment.name);
  }
  return false;
}

function TicketAttachmentVideoPreview({
  attachment,
  filePath,
  fallbackPaths,
  className,
  style,
  showPlayStopBar = false,
}: {
  attachment: Attachment;
  filePath: string;
  fallbackPaths: string[];
  className?: string;
  style?: CSSProperties;
  showPlayStopBar?: boolean;
}) {
  const videoFallbackPaths = useMemo(
    () =>
      [
        ...fallbackPaths,
        ...(attachment.mediaUrl ? [attachment.mediaUrl] : []),
        attachment.url,
      ].filter((path): path is string => Boolean(path?.trim())),
    [fallbackPaths, attachment.mediaUrl, attachment.url]
  );

  return (
    <AuthenticatedMediaVideo
      fileId={attachment.id}
      filePath={filePath}
      fallbackPaths={videoFallbackPaths}
      filenameHint={attachment.name}
      showPlayStopBar={showPlayStopBar}
      className={className}
      style={style}
    />
  );
}

function TicketAttachmentPdfPreview({
  attachment,
  filePath,
  fallbackPaths,
  className,
  style,
}: {
  attachment: Attachment;
  filePath: string;
  fallbackPaths: string[];
  className?: string;
  style?: CSSProperties;
}) {
  const candidates = useMemo(
    () =>
      [filePath, ...fallbackPaths, attachment.mediaUrl, attachment.url].filter(
        (path): path is string => Boolean(path?.trim())
      ),
    [filePath, fallbackPaths, attachment.mediaUrl, attachment.url]
  );
  const cacheKey = `pdf:${attachment.id || candidates.join('|')}`;
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSrc(null);

    void (async () => {
      const blobUrl = await getCachedPdfBlobObjectUrl(candidates, cacheKey);
      if (!cancelled) {
        setSrc(blobUrl);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  if (loading) {
    return (
      <div
        className={cn(
          'flex min-h-[200px] flex-1 flex-col items-center justify-center gap-2 text-sm text-muted-foreground',
          className
        )}
        style={style}
        aria-busy
      >
        <div className="h-8 w-8 animate-pulse rounded-full bg-muted-foreground/20" />
        <span>Loading PDF…</span>
      </div>
    );
  }

  if (!src) {
    return (
      <div
        className={cn(
          'flex min-h-[200px] flex-1 flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground',
          className
        )}
        style={style}
      >
        <FileText className="h-10 w-10 opacity-60" />
        <span>PDF could not be loaded.</span>
      </div>
    );
  }

  return (
    <iframe
      title={attachment.name}
      src={src}
      className={cn(
        'flex-1 min-h-0 min-w-0 rounded-md border border-border bg-background',
        className
      )}
      style={style}
    />
  );
}

export type AttachmentFullPreviewDialogProps = {
  attachment: Attachment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  videoShowPlayStopBar?: boolean;
};

/** Full-screen attachment preview (images, videos, PDFs). */
export function AttachmentFullPreviewDialog({
  attachment,
  open,
  onOpenChange,
  zoom = 1,
  onZoomChange,
  videoShowPlayStopBar = false,
}: AttachmentFullPreviewDialogProps) {
  const selectedPreviewPaths = attachment
    ? getAttachmentPreviewPaths(attachment)
    : null;
  const canZoom = attachment ? looksLikeImageAttachment(attachment) : false;

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          onOpenChange(false);
        }
      }}
    >
      <DialogContent className="flex h-[92vh] max-h-[92vh] w-[92vw] max-w-[92vw] flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="space-y-0 border-b border-border px-4 pb-3 pt-4 pr-20 text-left sm:text-left">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <DialogTitle className="line-clamp-2 min-w-0 flex-1 text-left text-sm font-medium leading-snug">
              {attachment?.name}
            </DialogTitle>
            {attachment && canZoom && onZoomChange ? (
              <div className="flex shrink-0 items-center gap-1 rounded-md border border-border bg-muted/40 px-1 py-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  aria-label="Zoom out"
                  onClick={() => onZoomChange(Math.max(0.5, zoom - 0.25))}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="min-w-[3rem] text-center text-xs tabular-nums text-muted-foreground">
                  {Math.round(zoom * 100)}%
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  aria-label="Zoom in"
                  onClick={() => onZoomChange(Math.min(3, zoom + 0.25))}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
            ) : null}
          </div>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-muted/50">
          {attachment && looksLikeImageAttachment(attachment) && selectedPreviewPaths && (
            <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-4">
              <div
                className="flex h-full w-full max-h-full max-w-full items-center justify-center"
                style={{
                  transform: zoom !== 1 ? `scale(${zoom})` : undefined,
                  transformOrigin: 'center center',
                }}
              >
                <AuthenticatedMediaImage
                  fileId={attachment.id}
                  filePath={selectedPreviewPaths.filePath}
                  fallbackPaths={selectedPreviewPaths.fallbackPaths}
                  alt={attachment.name}
                  className="block object-contain"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxWidth: '100%',
                    maxHeight: 'calc(92vh - 9rem)',
                    objectFit: 'contain',
                  }}
                />
              </div>
            </div>
          )}
          {attachment && looksLikeVideoAttachment(attachment) && selectedPreviewPaths && (
            <TicketAttachmentVideoPreview
              attachment={attachment}
              filePath={selectedPreviewPaths.filePath}
              fallbackPaths={selectedPreviewPaths.fallbackPaths}
              showPlayStopBar={videoShowPlayStopBar}
              className="flex-1"
              style={{ minHeight: 0, minWidth: 0 }}
            />
          )}
          {attachment && isPDF(attachment) && selectedPreviewPaths && (
            <TicketAttachmentPdfPreview
              attachment={attachment}
              filePath={selectedPreviewPaths.filePath}
              fallbackPaths={selectedPreviewPaths.fallbackPaths}
              className="flex-1"
              style={{ minHeight: 0, minWidth: 0 }}
            />
          )}
          {attachment &&
            !looksLikeImageAttachment(attachment) &&
            !looksLikeVideoAttachment(attachment) &&
            !isPDF(attachment) && (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center text-sm text-muted-foreground">
                <FileText className="h-12 w-12 opacity-60" />
                <p>Preview not available for this file type.</p>
              </div>
            )}
        </div>

        {attachment && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-card px-4 py-3 text-xs text-muted-foreground">
            <span className="min-w-0 break-all">
              {formatFileSize(attachment.size)} • {attachment.type}
            </span>
            <span className="shrink-0 text-right">
              {[attachment.uploadedBy, attachment.uploadedAt]
                .filter(Boolean)
                .join(' • ') || '—'}
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export interface TicketAttachmentCardsProps {
  attachments: Attachment[];
  /** ticket = full section header. compact = inside note replies. embedded = no header (parent already has one). */
  variant?: 'ticket' | 'compact' | 'embedded';
  videoShowPlayStopBar?: boolean;
}

/** Ticket + note attachments — same UI for Agent and Super Agent. */
export function SuperAgentAttachmentCards({
  attachments,
  variant = 'ticket',
  videoShowPlayStopBar = false,
}: TicketAttachmentCardsProps) {
  const [selectedAttachment, setSelectedAttachment] = useState<Attachment | null>(null);
  const [zoom, setZoom] = useState(1);

  if (attachments.length === 0) {
    return null;
  }

  const openPreview = (attachment: Attachment) => {
    setZoom(1);
    setSelectedAttachment(attachment);
  };

  const count = attachments.length;
  const multi = count > 1;
  const previewBoxHeight = multi
    ? 'h-[140px] max-h-[140px]'
    : 'h-[180px] max-h-[180px] sm:h-[200px] sm:max-h-[200px]';

  return (
    <>
      <div className="space-y-3 pt-1">
        {variant === 'ticket' ? (
          <div className="flex items-center gap-2 border-b border-border pb-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Paperclip className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">Attachments</h3>
              <p className="text-xs text-muted-foreground">
                {count === 1 ? '1 file' : `${count} files`}
              </p>
            </div>
          </div>
        ) : variant === 'compact' ? (
          <div className="flex items-center gap-2 border-b border-border pb-2">
            <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              Attachments ({count})
            </span>
          </div>
        ) : null}

        <div
          className={cn(
            'grid gap-3',
            count === 1 && 'grid-cols-1',
            count === 2 && 'grid-cols-2',
            count >= 3 && 'grid-cols-2 md:grid-cols-3'
          )}
        >
          {attachments.map(attachment => {
            const Icon = getFileIcon(attachment.type);
            const thumbImage = looksLikeImageAttachment(attachment);
            const canPreview =
              looksLikeImageAttachment(attachment) ||
              looksLikeVideoAttachment(attachment) ||
              isPDF(attachment);
            const { filePath, fallbackPaths } = getAttachmentPreviewPaths(attachment);

            return (
              <div
                key={attachment.id}
                className="min-w-0 overflow-hidden rounded-xl border border-border bg-card shadow-sm ring-1 ring-border/50 transition-shadow hover:shadow-md"
              >
                {thumbImage ? (
                  <button
                    type="button"
                    className="group relative block w-full overflow-hidden bg-muted/40"
                    onClick={() => openPreview(attachment)}
                  >
                    {attachment.visibility === 'internal' && (
                      <Badge
                        variant="secondary"
                        className="absolute left-2 top-2 z-10 px-1.5 py-0 text-[10px] font-medium"
                      >
                        Internal
                      </Badge>
                    )}
                    <div
                      className={cn(
                        'flex w-full items-center justify-center overflow-hidden p-2',
                        previewBoxHeight
                      )}
                    >
                      <AuthenticatedMediaImage
                        fileId={attachment.id}
                        filePath={filePath}
                        fallbackPaths={fallbackPaths}
                        alt={attachment.name}
                        className="max-h-full max-w-full rounded-md object-contain shadow-sm transition-transform duration-300 group-hover:scale-[1.01]"
                      />
                    </div>
                    <div className="pointer-events-none absolute inset-0 flex items-end justify-end gap-2 bg-gradient-to-t from-background/80 via-transparent to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
                      <span className="rounded-full bg-background/95 px-2 py-0.5 text-[11px] font-medium text-foreground shadow-sm ring-1 ring-border">
                        Preview
                      </span>
                    </div>
                  </button>
                ) : looksLikeVideoAttachment(attachment) ? (
                  <div
                    className={cn(
                      'relative flex w-full items-center justify-center overflow-hidden bg-neutral-950',
                      previewBoxHeight
                    )}
                  >
                    <TicketAttachmentVideoPreview
                      attachment={attachment}
                      filePath={filePath}
                      fallbackPaths={fallbackPaths}
                      className="h-full max-h-full w-full"
                    />
                  </div>
                ) : (
                  <div
                    className={cn(
                      'relative flex flex-col items-center justify-center gap-2 bg-gradient-to-b from-muted/50 to-muted/20 p-4',
                      multi ? 'min-h-[88px] sm:min-h-[100px]' : 'min-h-[100px] sm:min-h-[120px]'
                    )}
                  >
                    {attachment.visibility === 'internal' && (
                      <Badge
                        variant="secondary"
                        className="absolute left-2 top-2 z-10 px-1.5 py-0 text-[10px] font-medium"
                      >
                        Internal
                      </Badge>
                    )}
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-border bg-card shadow-sm">
                      <Icon className="h-7 w-7 text-muted-foreground" />
                    </div>
                    <p className="text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {attachment.type.split('/').pop() ?? 'File'}
                    </p>
                  </div>
                )}

                <div className="space-y-2 border-t border-border bg-card p-3 sm:p-4">
                  <div className="min-w-0">
                    <div className="flex items-start gap-2">
                      <Paperclip className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="break-words text-sm font-semibold leading-snug text-foreground">
                          {attachment.name}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {formatFileSize(attachment.size)}
                          {attachment.type ? ` · ${attachment.type}` : ''}
                        </p>
                      </div>
                    </div>
                  </div>

                  {canPreview ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1.5 px-3 rounded-lg border-primary/20 bg-primary/5 text-primary hover:bg-primary/15 hover:text-primary font-bold shadow-3xs transition-colors"
                        onClick={() => openPreview(attachment)}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Preview
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AttachmentFullPreviewDialog
        attachment={selectedAttachment}
        open={!!selectedAttachment}
        onOpenChange={open => {
          if (!open) {
            setSelectedAttachment(null);
          }
        }}
        zoom={zoom}
        onZoomChange={setZoom}
        videoShowPlayStopBar={videoShowPlayStopBar}
      />
    </>
  );
}

/** Same card UI as ticket attachments, for Public/Private note threads. */
export function AttachmentViewer({ attachments }: AttachmentViewerProps) {
  if (attachments.length === 0) return null;
  return (
    <SuperAgentAttachmentCards
      attachments={attachments}
      variant="compact"
    />
  );
}

interface AttachmentBadgeProps {
  count: number;
  onClick?: () => void;
}

export function AttachmentBadge({ count, onClick }: AttachmentBadgeProps) {
  if (count === 0) return null;

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
    >
      <Paperclip className="h-3 w-3" />
      {count}
    </button>
  );
}
